/** Destinations for in-app notification clicks. Keep in sync across bell + /notifications. */

function dataLink(data: Record<string, unknown> | null | undefined): string | null {
  return typeof data?.link === "string" && data.link.startsWith("/") ? data.link : null;
}

export function getNotificationUrl(
  type: string,
  data: Record<string, unknown> | null | undefined = {},
): string {
  const d = data ?? {};
  const appealUrl =
    typeof d.appealUrl === "string" && d.appealUrl.startsWith("/") ? d.appealUrl : "/account/appeals";

  switch (type) {
    case "NEW_MESSAGE": {
      const conversationId = typeof d.conversationId === "string" ? d.conversationId : "";
      const messageId = typeof d.messageId === "string" ? d.messageId : "";
      if (!conversationId) return "/messages";
      return `/messages?conversation=${encodeURIComponent(conversationId)}${
        messageId ? `&message=${encodeURIComponent(messageId)}` : ""
      }`;
    }
    case "BOOKING_REQUEST":
    case "BOOKING_CREATED":
      return dataLink(d) ?? "/tutor?tab=sessions";
    case "BOOKING_CONFIRMED":
    case "BOOKING_CANCELLED":
    case "SESSION_NO_SHOW":
    case "SESSION_REMINDER":
      return dataLink(d) ?? "/student?tab=sessions";
    case "FOCUS_DAILY_REMINDER":
    case "FOCUS_STREAK_SAVER":
    case "FOCUS_SESSION_COMPLETE": {
      if (dataLink(d)) return dataLink(d)!;
      if (typeof d.planId === "string" && d.planId) return `/study-plans/${d.planId}`;
      return "/student?focus=1";
    }
    case "BOOKING_RESCHEDULE_PROPOSED":
    case "BOOKING_RESCHEDULE_RESPONSE":
      return dataLink(d) ?? "/student?tab=sessions";
    case "CERTIFICATION_APPROVED":
    case "CERTIFICATION_REJECTED":
      return "/profile/certifications";
    case "STUDY_GROUP_INVITE":
      return d.inviteToken ? `/study-groups/join?token=${d.inviteToken}` : "/study-groups";
    case "STUDY_GROUP_MEMBER_JOINED":
    case "STUDY_GROUP_MEMBER_LEFT":
    case "STUDY_GROUP_UPDATE":
    case "STUDY_GROUP_CLOSED":
    case "STUDY_GROUP_COLYNX_ASSIGNED":
    case "STUDY_GROUP_SEEDED":
    case "STUDY_GROUP_LEAD_CLAIMED":
      return d.studyGroupId ? `/study-groups/${d.studyGroupId}` : "/study-groups";
    case "RECORDING_READY":
      if (d.studyGroupId) return `/study-groups/${d.studyGroupId}`;
      return d.sessionId ? `/sessions/${d.sessionId}/meeting` : "/student?tab=sessions";
    case "FORUM_REPLY":
    case "FORUM_MENTION":
      return d.topicId ? `/forums/${d.categorySlug || "general"}/${d.topicId}` : "/forums";
    case "MEMBER_REMOVAL_APPEAL":
    case "ACCOUNT_WARNING":
    case "ACCOUNT_SUSPENSION":
      return appealUrl;
    case "PAYMENT_SUCCESS":
      return dataLink(d) ?? "/student?tab=sessions";
    case "PAYMENT_FAILED":
    case "PAYMENT_REQUIRES_ACTION":
    case "REFUND_FAILED":
      return dataLink(d) ?? "/student?tab=wallet";
    case "SUBSCRIPTION_TRIAL_ENDING":
    case "SUBSCRIPTION_PAYMENT_ACTION_REQUIRED":
    case "SUBSCRIPTION_UPCOMING":
      return "/account/subscription";
    case "PAYOUT_FAILED":
    case "CONNECT_ACCOUNT_NEEDS_ATTENTION":
    case "CONNECT_ACCOUNT_DEAUTHORIZED":
    case "CONNECT_CAPABILITY_INACTIVE":
      return "/tutor?tab=earnings";
    case "DISPUTE_OPENED":
    case "DISPUTE_CLOSED":
    case "EARLY_FRAUD_WARNING":
    case "AD_SETTLEMENT":
      return "/admin/dashboard?tab=revenue";
    case "SUPPORT_TICKET_SUBMITTED":
      return d.ticketNumber || d.ticketId ? "/support" : "/support";
    case "VIDEO_PUBLISHED":
    case "VIDEO_LIKED":
    case "VIDEO_COMMENTED":
    case "VIDEO_UPLOADED":
      return d.videoId ? `/videos/${d.videoId}` : "/videos";
    case "NEW_FOLLOWER":
      return typeof d.username === "string" && d.username
        ? `/social-profile/${encodeURIComponent(d.username)}`
        : "/feed";
    case "POST_LIKED":
    case "POST_COMMENTED":
      return d.postId ? `/feed?post=${d.postId}` : "/feed";
    case "MILESTONE_CELEBRATED":
      return typeof d.username === "string" && d.username
        ? `/social-profile/${encodeURIComponent(d.username)}`
        : "/feed";
    case "AD_APPLICATION_RECEIVED":
    case "AD_APPLICATION_APPROVED":
    case "AD_APPLICATION_REJECTED":
    case "AD_CAMPAIGN_APPROVED":
    case "AD_CAMPAIGN_LIVE":
    case "AD_CAMPAIGN_PAYMENT_FAILED":
    case "AD_CAMPAIGN_REJECTED":
    case "AD_CAMPAIGN_PAUSED":
    case "AD_CAMPAIGN_ENDED":
    case "AD_CAMPAIGN_LOW_BALANCE":
      return "/advertiser/dashboard?tab=campaigns";
    case "ADMIN_TASK":
      if (typeof d.link === "string" && d.link.startsWith("/")) return d.link;
      return typeof d.tab === "string" && d.tab
        ? `/admin/dashboard?tab=${d.tab}`
        : "/admin/dashboard";
    case "SYSTEM_ANNOUNCEMENT":
    case "CONTENT_VIOLATION":
      return dataLink(d) ?? "/notifications";
    default:
      if (d.ticketNumber || d.type === "support_ticket") return "/support";
      return dataLink(d) ?? "/notifications";
  }
}
